import { POSTHOG_FEATURE_FLAGS, useFeatureFlagsReady, usePostHogFeatureFlag } from '../usePostHogFeatureFlag'
import { usePermissions } from '../usePermissions'

/**
 * Shared FGA gating for the `activity` domain.
 *
 * One domain covers the tag taxonomy, the checks raised against it and the events
 * recorded from them, so `can_read` gates all three reads — activity tags are fetched
 * app-wide, not only on the activity pages.
 *
 * While `internal_fga_orgv_activity` is off the backend skips its check, so every check
 * here falls open — the same window handling as `AccessRoute` and `resolveNavAccess`.
 *
 * Every check waits for `useFeatureFlagsReady()`. `usePostHogFeatureFlag` reads an
 * unresolved flag as `false`, which would fall open and render controls that then vanish
 * once the flag lands as on — the submit-tags button has no `AccessRoute` in front of it.
 */
export function useActivityAccess(): {
  canRead: boolean
  canCreateEvent: boolean
} {
  const { userHasPermissions } = usePermissions()
  const flagsReady = useFeatureFlagsReady()
  const internalFgaOrgvActivityFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_ACTIVITY)

  const isAllowed = (permission: string) =>
    flagsReady && (!internalFgaOrgvActivityFeatureFlag || userHasPermissions(permission))

  return {
    canRead: isAllowed('activity:can_read'),
    canCreateEvent: isAllowed('activity:can_create_event')
  }
}
